import { ref, computed, reactive } from 'vue'

// Reactive language state
const currentLanguage = ref('pl') // domyślny język polski

const SUPPORTED_LANGUAGES = ['pl', 'en', 'de', 'fr', 'cz', 'hu', 'it', 'ro']

// Loaded translations cache
const loadedTranslations = reactive({})

// Load translation file
async function loadTranslation(lang) {
  if (!loadedTranslations[lang]) {
    try {
      let module
      if (lang === 'pl') {
        module = await import('../lang/pl.json')
      } else if (lang === 'en') {
        module = await import('../lang/en.json')
      } else if (lang === 'de') {
        module = await import('../lang/de.json')
      } else if (lang === 'fr') {
        module = await import('../lang/fr.json')
      } else if (lang === 'cz') {
        module = await import('../lang/cz.json')
      } else if (lang === 'hu') {
        module = await import('../lang/hu.json')
      } else if (lang === 'it') {
        module = await import('../lang/it.json')
      } else if (lang === 'ro') {
        module = await import('../lang/ro.json')
      }
      loadedTranslations[lang] = module.default || module
    } catch (error) {
      console.error(`Failed to load translation for ${lang}:`, error)
      // Fallback to Polish if loading fails
      if (lang !== 'pl') {
        return loadTranslation('pl')
      }
    }
  }
  return loadedTranslations[lang]
}

// Get nested translation by key path (e.g., 'text.title')
function getNestedTranslation(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null
  }, obj)
}

// Translation function
async function translate(key, fallback = key) {
  const translations = await loadTranslation(currentLanguage.value)
  if (!translations) {
    return fallback
  }
  
  const translation = getNestedTranslation(translations, key)
  return translation !== null ? translation : fallback
}

// Composable hook
export function useTranslation() {
  // Set language from URL parameter
  const setLanguageFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const langParam = urlParams.get('lang')

    if (langParam && SUPPORTED_LANGUAGES.includes(langParam)) {
      currentLanguage.value = langParam
    } else {
      currentLanguage.value = 'pl' // domyślny język
    }
  }

  // Change language and update URL
  const setLanguage = (lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      currentLanguage.value = lang
      
      // Update URL parameter
      const url = new URL(window.location)
      url.searchParams.set('lang', lang)
      window.history.replaceState({}, '', url)
    }
  }

  // Reactive translation function
  const t = (key, fallback = key) => {
    return computed(async () => {
      return await translate(key, fallback)
    })
  }

  // Immediate translation function (for use in templates)
  const $t = async (key, fallback = key) => {
    return await translate(key, fallback)
  }

  // Initialize language from URL on first load
  setLanguageFromUrl()

  return {
    currentLanguage: computed(() => currentLanguage.value),
    setLanguage,
    setLanguageFromUrl,
    t,
    $t
  }
}

// Global instance for direct usage
export const { setLanguage, setLanguageFromUrl, t, $t } = useTranslation()
